import { useEffect, useState } from "react";
import { Phone, Mail, MapPin, Clock, MessageCircle, Send, Loader2, CheckCircle2, UserCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import PageHero from "@/components/site/PageHero";
import { Reveal } from "@/components/motion";
import { useSettings } from "@/context/SettingsContext";
import { submitEnquiry, getPage } from "@/lib/api";
import { getRecaptchaToken } from "@/lib/recaptcha";

const empty = { name: "", country: "", phone: "", whatsapp: "", email: "", travel_dates: "", adults: 2, children: 0, preferred_tour: "", message: "" };
const COUNTRIES = ["Malaysia", "Indonesia", "Singapore", "Thailand", "Vietnam", "Philippines", "Brunei", "India", "United Arab Emirates", "Saudi Arabia", "United Kingdom", "United States", "Canada", "Australia", "New Zealand", "Germany", "France", "Netherlands", "Spain", "Italy", "Japan", "South Korea", "China", "Hong Kong", "Taiwan", "South Africa", "Other"];

export default function Contact() {
  const { settings } = useSettings();
  const s = settings || {};
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [page, setPage] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { getPage("contact").then(setPage); }, []);

  const waNumber = (s.whatsapp || "+91 99976 11162").replace(/[^0-9]/g, "");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Please enter your name and email."); return; }
    setLoading(true);
    try {
      const token = await getRecaptchaToken(s?.recaptcha_site_key, "contact");
      await submitEnquiry({ ...form, adults: Number(form.adults) || 1, children: Number(form.children) || 0 }, token);
      setDone(true);
      toast.success("Message sent! We'll reply within 24 hours.");
    } catch (err) { toast.error("Could not send. Please WhatsApp us instead."); }
    finally { setLoading(false); }
  };

  return (
    <>
      <Seo
        title={page?.seo_title || "Contact Us | Legacy India Travel"}
        description={page?.seo_description || "Get in touch with Legacy India Travel to plan your tailor-made India tour. Call, WhatsApp or email us — we reply within 24 hours."}
      />
      <PageHero
        title={page?.hero_title || "Let's Plan Your Journey"}
        subtitle={page?.hero_subtitle || "Tell us your India dream — our travel designers will craft it into a personalised itinerary."}
        image={page?.hero_image || "https://images.unsplash.com/photo-1524492412937-b28074a5d7da?auto=format&fit=crop&w=1600&q=80"}
        crumbs={[{ label: "Contact" }]}
      />

      <section className="py-16 sm:py-24 bg-[#fdfbf7]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Info */}
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-serif text-3xl sm:text-4xl text-[#3b1a1a]">Talk to a local expert</h2>
              <p className="text-[#5a4b4b] mt-4 leading-relaxed whitespace-pre-line" data-testid="contact-intro">
                {page?.intro || "Based in Agra — the city of the Taj — we're here to help you experience the very best of India."}
              </p>
              <div className="mt-8 space-y-5">
                {s.director_name && (
                  <ContactRow icon={UserCircle} label={s.director_title || "Director"} value={s.director_name} testid="contact-director" />
                )}
                <ContactRow icon={Phone} label="Call Us" value={`${s.phone || ""}${s.phone_alt ? " / " + s.phone_alt : ""}`} href={`tel:${(s.phone || "").replace(/\s/g, "")}`} />
                <ContactRow icon={MessageCircle} label="WhatsApp" value={s.whatsapp} href={`https://wa.me/${waNumber}`} />
                <ContactRow icon={Mail} label="Email" value={s.email} href={`mailto:${s.email}`} />
                <ContactRow icon={Clock} label="Hours" value={s.office_hours} />
              </div>

              {/* Emergency contact — prominent */}
              {s.emergency_contact_name && s.emergency_contact_phone && (
                <div className="mt-6 border-l-2 border-[#7c1d1d] bg-[#7c1d1d]/5 p-4" data-testid="contact-emergency">
                  <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#7c1d1d] font-semibold">
                    <AlertCircle className="w-3.5 h-3.5" /> 24/7 Emergency Contact
                  </div>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="font-serif text-xl text-[#3b1a1a]">{s.emergency_contact_name}</span>
                    <a
                      href={`tel:${s.emergency_contact_phone.replace(/\s/g, "")}`}
                      className="text-[#7c1d1d] font-medium hover:underline"
                    >
                      {s.emergency_contact_phone}
                    </a>
                  </div>
                  <a
                    href={`https://wa.me/${s.emergency_contact_phone.replace(/[^0-9]/g, "")}`}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-2 text-xs text-[#14523d] hover:text-[#0f3d2c]"
                  >
                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp emergency line
                  </a>
                </div>
              )}
              {s.emergency_line && !s.emergency_contact_name && <p className="mt-6 text-sm text-[#14523d] border-l-2 border-[#14523d] pl-4">{s.emergency_line}</p>}
            </Reveal>

            {s.map_embed && (
              <div className="mt-8 border border-[#d4af37]/40 overflow-hidden aspect-[4/3]">
                <iframe title="Office location" src={s.map_embed} className="w-full h-full" loading="lazy" />
              </div>
            )}
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <div className="bg-white border border-[#d4af37]/40 p-7 sm:p-10">
              {done ? (
                <div className="text-center py-12" data-testid="contact-success">
                  <CheckCircle2 className="w-16 h-16 text-[#14523d] mx-auto" />
                  <h3 className="font-serif text-3xl text-[#3b1a1a] mt-5">{page?.success_title || "Thank you!"}</h3>
                  <p className="text-[#5a4b4b] mt-3 max-w-md mx-auto">{page?.success_body || "Your message has reached our team. We'll be in touch within 24 hours with a tailored plan."}</p>
                </div>
              ) : (
                <>
                  <div className="text-[11px] tracking-[0.3em] uppercase text-[#7c1d1d] font-semibold">Enquiry Form</div>
                  <h3 className="font-serif text-3xl text-[#3b1a1a] mt-2">{page?.form_title || "Send us your travel brief"}</h3>
                  <form onSubmit={onSubmit} data-testid="contact-form" className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Full Name *"><input data-testid="contact-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className="inp" placeholder="Your name" /></Field>
                    <Field label="Country">
                      <select data-testid="contact-country" value={form.country} onChange={(e) => set("country", e.target.value)} className="inp">
                        <option value="">Select country</option>
                        {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
                      </select>
                    </Field>
                    <Field label="Email *"><input data-testid="contact-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="inp" placeholder="you@email.com" /></Field>
                    <Field label="Phone"><input data-testid="contact-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="inp" placeholder="+ country code" /></Field>
                    <Field label="WhatsApp"><input value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className="inp" placeholder="+ country code" /></Field>
                    <Field label="Travel Dates"><input value={form.travel_dates} onChange={(e) => set("travel_dates", e.target.value)} className="inp" placeholder="e.g. Dec 2025" /></Field>
                    <Field label="Adults"><input type="number" min={1} value={form.adults} onChange={(e) => set("adults", e.target.value)} className="inp" /></Field>
                    <Field label="Children"><input type="number" min={0} value={form.children} onChange={(e) => set("children", e.target.value)} className="inp" /></Field>
                    <div className="sm:col-span-2"><Field label="Preferred Tour"><input value={form.preferred_tour} onChange={(e) => set("preferred_tour", e.target.value)} className="inp" placeholder="e.g. Golden Triangle" /></Field></div>
                    <div className="sm:col-span-2"><Field label="Message"><textarea data-testid="contact-message" rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} className="inp resize-none" placeholder="Tell us about your dream trip..." /></Field></div>
                    <button type="submit" disabled={loading} data-testid="contact-submit" className="sm:col-span-2 inline-flex items-center justify-center gap-2 bg-[#7c1d1d] hover:bg-[#5a1515] disabled:opacity-60 text-white px-8 py-4 text-xs tracking-[0.26em] uppercase transition-colors">
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {loading ? "Sending..." : "Send Enquiry"}
                    </button>
                    {s?.recaptcha_site_key && (
                      <p className="sm:col-span-2 text-[10px] text-[#8a7c66] leading-relaxed">
                        Protected by reCAPTCHA — Google <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">Privacy</a> · <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline">Terms</a> apply.
                      </p>
                    )}
                  </form>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Our Offices — 3 addresses grid */}
        {Array.isArray(s.offices) && s.offices.length > 0 && (
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 mt-16 sm:mt-24" data-testid="contact-offices">
            <Reveal>
              <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                <div>
                  <div className="text-[11px] tracking-[0.3em] uppercase text-[#7c1d1d] font-semibold">Our Offices</div>
                  <h2 className="font-serif text-3xl sm:text-4xl text-[#3b1a1a] mt-1">Visit us across India</h2>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {s.offices.map((o, i) => (
                  <div
                    key={i}
                    data-testid={`contact-office-${i}`}
                    className="bg-white border border-[#d4af37]/30 p-6 relative overflow-hidden"
                  >
                    <span className="absolute top-0 left-0 h-1 w-16 bg-[#7c1d1d]" />
                    <div className="text-[10px] tracking-[0.28em] uppercase text-[#14523d] font-semibold mt-2">{o.label || "Office"}</div>
                    <div className="font-serif text-2xl text-[#3b1a1a] mt-1 leading-tight">{o.name || "Legacy India Travel"}</div>
                    <div className="mt-4 flex items-start gap-3 text-[#4a4a4a] text-sm leading-relaxed">
                      <MapPin className="w-4 h-4 text-[#d4af37] shrink-0 mt-0.5" />
                      <span className="whitespace-pre-line">{o.address}</span>
                    </div>
                    {o.phone && (
                      <a href={`tel:${o.phone.replace(/\s/g, "")}`} className="mt-3 flex items-center gap-2 text-sm text-[#7c1d1d] hover:text-[#5a1515]">
                        <Phone className="w-4 h-4" /> {o.phone}
                      </a>
                    )}
                    {o.email && (
                      <a href={`mailto:${o.email}`} className="mt-2 flex items-center gap-2 text-sm text-[#7c1d1d] hover:text-[#5a1515]">
                        <Mail className="w-4 h-4" /> {o.email}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        )}
      </section>
    </>
  );
}

function ContactRow({ icon: Icon, label, value, href, testid }) {
  const inner = (
    <div className="flex items-start gap-4" {...(testid ? { "data-testid": testid } : {})}>
      <span className="w-11 h-11 shrink-0 border border-[#d4af37]/50 grid place-items-center text-[#7c1d1d]"><Icon className="w-5 h-5" /></span>
      <div><div className="text-[10px] tracking-[0.22em] uppercase text-[#5a4b4b]">{label}</div><div className="text-[#3b1a1a] font-medium">{value}</div></div>
    </div>
  );
  return href ? <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="block hover:opacity-80 transition-opacity">{inner}</a> : inner;
}

function Field({ label, children }) {
  return <label className="block"><span className="block text-[10px] tracking-[0.2em] uppercase text-[#5a4b4b] mb-1.5">{label}</span>{children}</label>;
}
